
import { Component, Prop, Watch } from 'vue-property-decorator';
import Page from '@/Page.vue';
import DatePicker from '@/libs/common/components/DatePicker.vue';
import moment from 'moment';
import { GanttGranularity } from '@/libs/tpm-analytics/core/gantt-timeline.types';

@Component({
    components: {
        DatePicker,
    },
})
export default class GanttToolbar extends Page {
    @Prop({ default: 'hourly' }) granularity!: GanttGranularity;
    @Prop({ required: true }) fromDate!: string; // ISO string
    @Prop({ required: true }) toDate!: string; // ISO string
    @Prop({ default: 1.0 }) pixelsPerHourBase!: number; // Základní hodnota (30px)
    @Prop({ default: false }) showExport!: boolean;

    // Internal state
    internalGranularity: GanttGranularity = this.granularity;
    internalFromDate: string = this.fromDate;
    internalToDate: string = this.toDate;
    
    // Zoom
    zoomLevel = 1.0;
    minZoom = 0.25; // 25%
    maxZoom = 4.0; // 400%

    created() {
        this.internalGranularity = this.granularity;
        this.internalFromDate = this.fromDate;
        this.internalToDate = this.toDate;
    }

    /**
     * Watch změny granularity
     */
    @Watch('internalGranularity')
    onGranularityChanged(newValue: GanttGranularity) {
        this.$emit('update:granularity', newValue);
    }

    /**
     * Watch změny from date
     */
    @Watch('internalFromDate')
    onFromDateChanged(newValue: string) {
        if (newValue && moment(newValue).isValid()) {
            this.$emit('update:fromDate', moment(newValue).startOf('day').toISOString());
        }
    }

    /**
     * Watch změny to date
     */
    @Watch('internalToDate')
    onToDateChanged(newValue: string) {
        if (newValue && moment(newValue).isValid()) {
            this.$emit('update:toDate', moment(newValue).endOf('day').toISOString());
        }
    }

    /**
     * Watch props změny
     */
    @Watch('granularity')
    onPropGranularityChanged(newValue: GanttGranularity) {
        this.internalGranularity = newValue;
    }

    @Watch('fromDate')
    onPropFromDateChanged(newValue: string) {
        this.internalFromDate = newValue;
    }

    @Watch('toDate')
    onPropToDateChanged(newValue: string) {
        this.internalToDate = newValue;
    }

    /**
     * Zoom in
     */
    zoomIn() {
        const newZoom = Math.min(this.maxZoom, this.zoomLevel + 0.25);
        this.zoomLevel = newZoom;
        this.$emit('update:zoom', newZoom);
    }

    /**
     * Zoom out
     */
    zoomOut() {
        const newZoom = Math.max(this.minZoom, this.zoomLevel - 0.25);
        this.zoomLevel = newZoom;
        this.$emit('update:zoom', newZoom);
    }

    /**
     * Reset zoom
     */
    resetZoom() {
        this.zoomLevel = 1.0;
        this.$emit('update:zoom', 1.0);
    }

    /**
     * Přejít na dnešní den
     */
    goToToday() {
        const today = moment().startOf('day');
        const weekAgo = moment().subtract(3, 'days').startOf('day');
        const weekAhead = moment().add(4, 'days').endOf('day');

        this.internalFromDate = weekAgo.toISOString();
        this.internalToDate = weekAhead.toISOString();

        this.$emit('scroll-to-date', today.toDate());
    }

    /**
     * Export do PNG (placeholder)
     */
    exportPng() {
        this.$emit('export', 'png');
        // TODO: Implementace exportu
        this.toast(this.$t('common.notImplementedYet') as string);
    }

    /**
     * Export do PDF (placeholder)
     */
    exportPdf() {
        this.$emit('export', 'pdf');
        // TODO: Implementace exportu
        this.toast(this.$t('common.notImplementedYet') as string);
    }

    /**
     * Toast notifikace
     */
    toast(text: string) {
        // Použijeme parent komponentu pro toast
        this.$emit('toast', text);
    }
}
